import { useCallback, useEffect, useMemo, useState } from 'react';
import { Template_TemplateType } from '@weave/schema-gen-ts/dist/schemas/reviews/landing-page-templates/v1/landing_page_template_service.pb';
import { AnimatePresence, motion } from 'motion/react';
import { useTranslation } from '@frontend/i18n';
import { useAppScopeStore } from '@frontend/scope';
import {
  SettingsLink,
  StaticSettingsRoutes,
  useSettingsNavigate,
  useSettingsLocation,
} from '@frontend/settings-routing';
import { TemplatesPrefixes } from '@frontend/tracking-prefixes';
import { theme } from '@frontend/theme';
import {
  Button,
  Chip,
  ConfirmationModal,
  DropdownField,
  LinkIconSmall,
  SpinningLoader,
  TextLink,
  Text,
  useAlert,
  CustomChangeEvent,
  useFormField,
} from '@frontend/design-system';
import {
  ContentNotFetchedPlaceholder,
  GoogleReviewPlaceholder,
  FacebookReviewPlaceholder,
  LegacyReviewPage,
} from './assets';
import {
  useDeleteLandingPageTemplate,
  useGetReviewsInvitationTemplates,
  useGetReviewTemplates,
} from './hooks/use-landing-page-queries';
import { ReviewLinkIframe } from './review-link-iframe';
import {
  chipWithLinkStyles,
  iconStyles,
  landingPageMenuStyles,
  linkStyles,
  imgContentPlaceholderStyles,
  overlayContainerStyles,
  overlayFooterContainerStyles,
  overlayStyle,
  reviewLinkStyles,
} from './review-link-styles';
import { useReviewLandingPageStore, useTemplateFormShallowStore } from './stores/index';
import { AffiliateReviewIds, ReviewLandingPageTemplate } from './types';

type ReviewLinkProps = {
  isLegacyReviewTemplate?: boolean;
  onReviewsLandingPageTemplateChange?: (reviewsLandingPageTemplateId: string) => void;
  reviewsLandingPageId?: string;
  saveToTemplateFormStore: () => void;
  templateLocations?: string[];
  updateIsLegacyReviewTemplateState: (isLegacy: boolean) => void;
  variant: 'auto-message' | 'manual-template';
};

type RouteNavigateContext = {
  returnTo: string;
};

const LEGACY_REVIEW_PAGE_ID = 'Legacy Template';

export const ReviewLink = ({
  saveToTemplateFormStore,
  templateLocations,
  updateIsLegacyReviewTemplateState,
  isLegacyReviewTemplate,
  onReviewsLandingPageTemplateChange,
  reviewsLandingPageId,
  variant,
}: ReviewLinkProps) => {
  const { navigate } = useSettingsNavigate<RouteNavigateContext>();
  const {
    current: { pathname: currentPathname },
  } = useSettingsLocation();
  const { t } = useTranslation('templates-settings');
  const alert = useAlert();

  const { selectedLocationIds } = useAppScopeStore();
  const [legacyReviewImgState, setLegacyReviewImgState] = useState<'loading' | 'loaded' | 'error'>();
  const { templateId } = useTemplateFormShallowStore('templateId');
  const {
    reviewLandingPageId: landingPageTemplateIdFromStore,
    status: landingPageStatus,
    setReviewLandingPageDataInStore,
    resetReviewLandingPageStatus,
  } = useReviewLandingPageStore();

  const {
    landingPageReviewTemplates,
    affiliateReviewTemplates,
    isLoading: isGetReviewTemplatesLoading,
    isError: isGetReviewTemplatesError,
    isReviewSettingsError,
  } = useGetReviewTemplates({
    selectedLocationIds: templateLocations?.length ? templateLocations : selectedLocationIds,
    handleListLandingPageTemplatesError: () => {
      alert.error(t('Error fetching landing page templates.'));
    },
    handleListAffiliateReviewLinksError: () => {
      alert.error(t('Error fetching affiliate review links.'));
    },
  });

  const { data: reviewsInvitationTemplates, isLoading: isGetReviewsInvitationTemplateLoading } =
    useGetReviewsInvitationTemplates({
      selectedLocationIds,
      enabled: !!templateId,
      handleErrorResponse: () => {
        if (landingPageStatus === 'existing')
          alert.error(t('Error fetching review invitations. Previously used landing page is selected for you.'));
      },
    });

  const { mutate: deleteTemplate, isLoading: isDeleteTemplateLoading } = useDeleteLandingPageTemplate({
    groupIds: templateLocations,
    handleSuccessResponse: () => {
      setReviewLandingPageDataInStore(undefined, 'deleted');
      alert.success(t('Landing page template deleted successfully!'));
    },
    handleErrorResponse: () => {
      alert.error(t('Error deleting landing page template!'));
    },
  });

  const isLoading =
    isGetReviewTemplatesLoading ||
    isGetReviewsInvitationTemplateLoading ||
    isDeleteTemplateLoading ||
    legacyReviewImgState === 'loading';

  const isLandingPageIdValid = useCallback(
    (landingPageId: string | undefined) =>
      !!(
        landingPageReviewTemplates?.find((template) => template.templateId === landingPageId) ||
        affiliateReviewTemplates?.find((template) => template.templateId === landingPageId)
      ),
    [landingPageReviewTemplates, affiliateReviewTemplates]
  );

  // TODO: Move this to Global-info-banner once the new banner is live.
  useEffect(() => {
    if (isLoading || isReviewSettingsError || landingPageStatus !== 'existing') return;

    const isSingleLocationSelected = templateLocations?.length === 1;

    if (affiliateReviewTemplates?.length === 0) {
      const noGoogleAndFacebookReviewMessage = `No integration found for Google or Facebook 
      at the selected ${isSingleLocationSelected ? 'location' : 'locations'}. 
      Please use the Private or Custom options to collect reviews.`;

      alert.warning(t('{{noGoogleAndFacebookReviewMessage}}', { noGoogleAndFacebookReviewMessage }));
    } else if (
      affiliateReviewTemplates?.length === 1 &&
      affiliateReviewTemplates[0].templateId !== AffiliateReviewIds.GOOGLE
    ) {
      const noGoogleReviewMessage = `No integration found for Google at the selected ${
        isSingleLocationSelected ? 'location' : 'locations'
      }. 
      Please use the Private or Custom options to collect reviews. 
      Avoid using review links with Google review button to collect reviews.`;

      alert.warning(t('{{noGoogleReviewMessage}}', { noGoogleReviewMessage }));
    } else if (
      affiliateReviewTemplates?.length === 1 &&
      affiliateReviewTemplates[0].templateId !== AffiliateReviewIds.FACEBOOK
    ) {
      const noFacebookReviewMessage = `No integration found for Facebook at the selected ${
        isSingleLocationSelected ? 'location' : 'locations'
      }. 
      Please use the Private or Custom options to collect reviews. 
      Avoid using review links with Facebook review button to collect reviews.`;

      alert.warning(t('{{noFacebookReviewMessage}}', { noFacebookReviewMessage }));
    }
  }, [affiliateReviewTemplates, isReviewSettingsError, isLoading, templateLocations]);

  useEffect(() => {
    if (!templateId) updateIsLegacyReviewTemplateState(true);
    else if (!!reviewsInvitationTemplates?.length) {
      const savedReviewSiteTemplateId = reviewsInvitationTemplates?.find((invitation) => {
        return invitation.templateId === templateId;
      })?.reviewSite?.templateId;
      updateIsLegacyReviewTemplateState(!savedReviewSiteTemplateId);
    }

    return () => {
      resetReviewLandingPageStatus();
    };
  }, [reviewsInvitationTemplates, templateId]);

  useEffect(() => {
    if (
      (!landingPageReviewTemplates?.length ||
        !affiliateReviewTemplates ||
        (!reviewsInvitationTemplates?.length && !!templateId)) &&
      landingPageStatus !== 'deleted'
    )
      return;

    // To handle newly created landingPage template after navigating back from the composer.
    if (!!landingPageTemplateIdFromStore && landingPageStatus === 'new') {
      resetReviewLandingPageStatus();
      return;
    }

    // For Edit mode
    if (templateId) {
      // Check to see if the review invitation for the current template has a reviewSiteTemplateId.
      const savedReviewSiteTemplateId = reviewsInvitationTemplates?.find((invitation) => {
        return invitation.templateId === templateId;
      })?.reviewSite?.templateId;
      // Check to see if savedReviewSiteTemplateId is present in list of saved landingPageTemplates for the location.
      if (isLandingPageIdValid(savedReviewSiteTemplateId)) {
        setReviewLandingPageDataInStore(savedReviewSiteTemplateId ?? '');
        return;
      }
      if (!savedReviewSiteTemplateId && landingPageStatus === 'existing') {
        setReviewLandingPageDataInStore(LEGACY_REVIEW_PAGE_ID);
        return;
      }
      if (landingPageStatus == 'existing') {
        alert.info(
          t(
            'Landing page associated with the template is deleted or broken. Previously used or default page is selected for you.'
          )
        );
      }
    }

    if (reviewsLandingPageId && isLandingPageIdValid(reviewsLandingPageId)) {
      setReviewLandingPageDataInStore(reviewsLandingPageId);
    }

    // For create mode or when savedReviewSiteTemplateId in editMode is not valid.
    if (isLandingPageIdValid(landingPageTemplateIdFromStore)) return;
    const defaultLandingPageId =
      landingPageReviewTemplates?.find((item) => item.type === Template_TemplateType.DEFAULT)?.templateId ||
      landingPageReviewTemplates?.[0].templateId;
    setReviewLandingPageDataInStore(defaultLandingPageId ?? '');
    return;
  }, [
    JSON.stringify(reviewsInvitationTemplates),
    landingPageReviewTemplates,
    affiliateReviewTemplates,
    templateId,
    reviewsLandingPageId,
  ]);

  const reviewTemplateDropdownFieldProps = useFormField(
    {
      type: 'dropdown',
      value:
        isLandingPageIdValid(landingPageTemplateIdFromStore) ||
        (isLegacyReviewTemplate && landingPageTemplateIdFromStore === LEGACY_REVIEW_PAGE_ID)
          ? landingPageTemplateIdFromStore
          : '',
    },
    [landingPageTemplateIdFromStore, isLandingPageIdValid, isLegacyReviewTemplate]
  );

  const selectedReviewTemplateId = reviewTemplateDropdownFieldProps.value;
  const isLegacyReviewPageSelected = selectedReviewTemplateId === LEGACY_REVIEW_PAGE_ID && !!templateId;
  const selectedReviewTemplate = useMemo<ReviewLandingPageTemplate | undefined>(() => {
    const selectedLandingPageTemplate = landingPageReviewTemplates?.find(
      (data) => data?.templateId === selectedReviewTemplateId
    );
    if (selectedLandingPageTemplate) return selectedLandingPageTemplate;
    else return affiliateReviewTemplates?.find((data) => data.templateId === selectedReviewTemplateId);
  }, [selectedReviewTemplateId, landingPageReviewTemplates, affiliateReviewTemplates]);

  const [showReviewLinkOverlay, setShowReviewLinkOverlay] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

  const imagePlaceholderSrc = useMemo(() => {
    switch (selectedReviewTemplate?.type) {
      case 'GOOGLE':
        return GoogleReviewPlaceholder;
      case 'FACEBOOK':
        return FacebookReviewPlaceholder;
      default:
        return ContentNotFetchedPlaceholder;
    }
  }, [selectedReviewTemplate?.type]);

  const reviewTemplatePreviewData = useMemo(() => {
    switch (selectedReviewTemplate?.type) {
      case 'GOOGLE':
        return {
          label: 'google.r',
          link: selectedReviewTemplate?.previewLink,
        };
      case 'FACEBOOK':
        return {
          label: 'facebook.r',
          link: selectedReviewTemplate?.previewLink,
        };
      case 'DEFAULT':
      case 'CUSTOM':
        return {
          label: 'wc3io.w',
          link: selectedReviewTemplate?.previewLink,
        };
      default:
        return;
    }
  }, [selectedReviewTemplate?.type, selectedReviewTemplate?.previewLink]);

  const handleReviewPageEditorOpen = (
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement, MouseEvent>,
    mode: 'create' | 'edit'
  ) => {
    event.preventDefault();
    saveToTemplateFormStore();
    let editorPath;

    // to remove 'settings' from current path url and build editor path as current.pathname
    // returns path with 'settings/' prepended.
    switch (variant) {
      case 'auto-message': {
        editorPath = `${currentPathname.replace('settings/', '').replace('/editor', '')}/reviews`;
        break;
      }
      case 'manual-template': {
        editorPath = `${currentPathname.replace('settings/', '')}/review-landing-page-editor`;
        break;
      }
    }

    if (mode === 'edit' && selectedReviewTemplateId) {
      editorPath += `/${selectedReviewTemplateId}`;
    }
    navigate({ to: editorPath as StaticSettingsRoutes, context: { returnTo: currentPathname } });
  };

  const handleDeleteTemplateConfirmation = () => {
    if (selectedReviewTemplate?.type !== Template_TemplateType.DEFAULT) {
      deleteTemplate({ templateId: selectedReviewTemplateId });
    } else {
      alert.error(t('Default template cannot be deleted.'));
    }
  };

  const showAffiliateReviewBackground =
    !isLoading &&
    !isGetReviewTemplatesError &&
    landingPageStatus !== 'deleted' &&
    ['GOOGLE', 'FACEBOOK'].includes(selectedReviewTemplate?.type ?? '');

  return (
    <div
      css={reviewLinkStyles(showAffiliateReviewBackground)}
      onMouseMove={() => setShowReviewLinkOverlay(true)}
      onMouseLeave={() => setShowReviewLinkOverlay(false)}
    >
      {/**
       * TODO handle all img load and error state.
       * Current logic works good as they only load svgs from the build. (expect legacyReviewImg)
       * Should revisit moving svgs to cdn.
       */}
      {isLoading ? (
        <SpinningLoader size='medium' />
      ) : isGetReviewTemplatesError || legacyReviewImgState === 'error' ? (
        <img css={imgContentPlaceholderStyles} src={ContentNotFetchedPlaceholder} alt={t('No content')} />
      ) : isLegacyReviewPageSelected ? (
        <img
          css={imgContentPlaceholderStyles}
          src={LegacyReviewPage}
          alt={t('Legacy review')}
          onLoadStart={() => setLegacyReviewImgState('loading')}
          onLoad={() => setLegacyReviewImgState('loaded')}
          onError={() => setLegacyReviewImgState('error')}
        />
      ) : selectedReviewTemplate?.htmlConfig && selectedReviewTemplate.previewLink ? ( // Templates with htmlConfig/renderHTML is considered to be custom landingPage Templates.
        <ReviewLinkIframe src={selectedReviewTemplate.previewLink} />
      ) : (
        <img css={imgContentPlaceholderStyles} src={imagePlaceholderSrc} alt='Review page preview' /> // Affiliate link template
      )}

      {/* Overlay Component */}
      <AnimatePresence>
        {((showReviewLinkOverlay && !isLoading) || isLegacyReviewPageSelected) && (
          <motion.div
            css={overlayContainerStyles}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
          >
            <div css={overlayStyle(isLegacyReviewPageSelected)}>
              <DropdownField
                name='Review Templates'
                label=''
                css={landingPageMenuStyles}
                {...reviewTemplateDropdownFieldProps}
                onChange={(event: CustomChangeEvent<string>) => {
                  if (event?.value) {
                    setReviewLandingPageDataInStore(event.value);
                    onReviewsLandingPageTemplateChange?.(event.value);
                  }
                  reviewTemplateDropdownFieldProps.onChange(event);
                }}
                disabled={isLoading}
              >
                <DropdownField.Option
                  key={'create-new-page'}
                  value='New page'
                  css={{ '&:hover': { cursor: 'pointer' } }}
                  disabled
                  data-trackingid={`${TemplatesPrefixes.LandingPageEditor}-create-new-page`}
                >
                  <SettingsLink
                    to={'' as any}
                    css={linkStyles}
                    onClick={(e) => handleReviewPageEditorOpen(e, 'create')}
                  >
                    Create New Page
                  </SettingsLink>
                </DropdownField.Option>
                {landingPageReviewTemplates?.map(({ templateId, title, type }) => (
                  <DropdownField.Option
                    key={templateId}
                    value={templateId}
                    tooltipPlacement='right'
                    data-trackingid={`${TemplatesPrefixes.LandingPageEditor}-select-${
                      type === Template_TemplateType.DEFAULT ? 'default' : 'existing-custom'
                    }-page`}
                  >
                    {title}
                  </DropdownField.Option>
                ))}
                {affiliateReviewTemplates?.map(({ templateId, title }, index) => (
                  <DropdownField.Option
                    key={templateId}
                    value={templateId}
                    css={!index && { borderTop: `1px solid ${theme.colors.neutral20}` }}
                    tooltipPlacement='right'
                    data-trackingid={`${TemplatesPrefixes.LandingPageEditor}-select-platform-default-page-${title}`}
                  >
                    {title}
                  </DropdownField.Option>
                ))}
              </DropdownField>

              {!isGetReviewTemplatesError && selectedReviewTemplateId && !isLegacyReviewPageSelected && (
                <div css={overlayFooterContainerStyles}>
                  {/* Preview link button component */}
                  {reviewTemplatePreviewData?.link && (
                    <Chip css={chipWithLinkStyles}>
                      <LinkIconSmall css={{ color: theme.colors.black }} />
                      <TextLink href={reviewTemplatePreviewData.link} target='_blank' rel='noopener noreferrer'>
                        {reviewTemplatePreviewData.label}
                      </TextLink>
                    </Chip>
                  )}

                  {/* Templates with htmlConfig are landing page templates only they can be edited or deleted */}
                  {selectedReviewTemplate?.htmlConfig && (
                    <div css={{ display: 'flex', gap: theme.spacing(1) }}>
                      <Button
                        iconName='edit-small'
                        css={iconStyles}
                        disabled={isDeleteTemplateLoading}
                        onClick={(e) => handleReviewPageEditorOpen(e, 'edit')}
                        data-trackingid={`${TemplatesPrefixes.LandingPageEditor}-edit-selected-custom-page`}
                      />
                      {selectedReviewTemplate?.type !== 'DEFAULT' && ( // Hide Delete for Default Template
                        <Button
                          iconName='trash-small'
                          css={iconStyles}
                          onClick={() => setShowDeleteModal(true)}
                          disabled={isLoading}
                          data-trackingid={`${TemplatesPrefixes.LandingPageEditor}-delete-selected-custom-page`}
                        />
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Delete Template Modal */}
      <ConfirmationModal
        show={showDeleteModal}
        destructive
        onClose={() => {
          setShowReviewLinkOverlay(false);
          setShowDeleteModal(false);
        }}
        onConfirm={handleDeleteTemplateConfirmation}
        confirmLabel={t('Delete This Page')}
        cancelLabel={t('Cancel')}
        title={t('Are you Sure?')}
        modalCss={{ '.confirmationTextStyle': { margin: theme.spacing(-1.25, 0.5, -0.2, 0) } }}
        children={
          <Text className='confirmationTextStyle' size='medium'>
            {t('If you proceed')}
            <strong> {t('all links')} </strong>
            {t('associated with page')}
            <strong> {t('will be broken')} </strong>
            {t('and cannot be fixed.')}
          </Text>
        }
      />
    </div>
  );
};
